import { isRunnerPausedFromPage } from './claude-fleet.runner-actions'
import {
  isRunnerOwner,
  runnerFleetAndOwnLabel,
  runnerItemsTodayLabel,
  runnerPausedReason,
  runnerPausedShort
} from './claude-fleet.runners'
import { runnerHealthItems } from './claude-fleet.runner-health'
import { runnerBusy, runnerTail } from './claude-fleet.runner-live'
import type { FleetRunner } from './claude-fleet.types'

/** The sidebar runner card shows the engineer's own runner as the workspace's status line does (bin/runner-statusline.sh). */

/** Opens a terminal on this computer running bin/runner.sh, through the workspace's tattle-claude:// handler. */
export const RUNNER_START_URL = 'tattle-claude://runner/start'

/**
 * How long the card says Starting after Start: the terminal opens, the launcher pulls any updates waiting (several
 * minutes after a busy day of merges), /runner takes the machine, then the first heartbeat a minute later.
 */
export const RUNNER_STARTING_MS = 10 * 60 * 1000

/** attention: live, but a gate or a warning holds it back. */
export type RunnerCardState = 'off' | 'starting' | 'running' | 'attention'

/** The hover: a headline, then labelled facts, then the window's last lines, then where a click goes. */
export interface RunnerCardHover {
  title: string
  /** Sentences under the title, for a runner that is not live. */
  notes?: string[]
  facts?: { label: string; value: string }[]
  /** The last lines the window or the watch screen shows, under a heading saying which. */
  tail?: { title: string; lines: string[] }
  hint?: string
}

export interface RunnerCardView {
  state: RunnerCardState
  /** The card's own words, short enough for the sidebar's width: "2 running", "Idle", "Low disk", "Runner off". */
  label: string
  hover: RunnerCardHover
  /** The hover as plain lines, one fact per line, for the card's accessible name. */
  lines: string[]
  /** A gate or warning holds a live runner back; a pause set from a page is the engineer's own choice, so not that. */
  warning?: boolean
  /** The window is restarting onto an update once its running jobs finish: routine, so no warning. */
  updating?: boolean
}

/** The hover's lines, in the order it shows them. */
export function runnerCardHoverLines(hover: RunnerCardHover): string[] {
  return [
    hover.title,
    ...(hover.notes ?? []),
    ...(hover.facts ?? []).map(fact => `${fact.label}: ${fact.value}`),
    ...(hover.tail ? [hover.tail.title, ...hover.tail.lines.map(line => `  ${line}`)] : []),
    ...(hover.hint ? [hover.hint] : [])
  ]
}

function withHover(view: Omit<RunnerCardView, 'lines'>): RunnerCardView {
  return { ...view, lines: runnerCardHoverLines(view.hover) }
}

/**
 * Whether the sidebar's runner card is for this viewer: someone on the Engineering Team page, or whose machine is
 * already a runner (all the fleet has while it cannot read the page), so CS, sales and product never meet a runner
 * they cannot use.
 */
export function runnerCardEligible(
  runners: FleetRunner[],
  email: string | null | undefined,
  viewerEngineer: boolean | null
): boolean {
  return viewerEngineer === true || myRunner(runners, email) !== null
}

/** The engineer's live runner, else their most recently seen one; null when they have none the fleet saw. */
export function myRunner(
  runners: FleetRunner[],
  email: string | null | undefined
): FleetRunner | null {
  const mine = runners.filter(runner => isRunnerOwner(runner.owner, email))
  mine.sort((a, b) => Number(b.live) - Number(a.live) || b.lastSeen.localeCompare(a.lastSeen))
  return mine[0] ?? null
}

/** What the card shows for `runner` (null: none), `startedAt` being when this page last clicked Start. */
export function runnerCardView(
  runner: FleetRunner | null,
  options: { now: number; startedAt: number | null }
): RunnerCardView {
  if (runner?.live) return liveView(runner, options.now)
  if (options.startedAt !== null && options.now - options.startedAt < RUNNER_STARTING_MS) {
    return withHover({
      state: 'starting',
      label: 'Starting…',
      hover: {
        title: 'Your runner window is opening. This shows it once it checks in, within a minute or two.',
        notes: ['Nothing opened, or you cancelled the prompt? Click here to open it again, or ✕ to stop waiting.']
      }
    })
  }
  return withHover({
    state: 'off',
    label: 'Runner off',
    hover: {
      title: 'Opens your runner on this computer, with your saved settings.',
      notes: [
        'It needs the tattle-claude link handler, which /runner setup offers to install (or bin/install-continue-handler.sh).'
      ]
    }
  })
}

function liveView(runner: FleetRunner, now: number): RunnerCardView {
  const health = runnerHealthItems(runner.health, now)
  const paused = runnerPausedReason(runner)
  const busy = runnerBusy(runner)
  const summary = busy.total ? `${busy.total} running` : 'Idle'
  const usage = runnerUsageLine(runner.health, now, runner.usageAccount)
  const hover: RunnerCardHover = {
    title: runnerBusyTitle(busy),
    facts: [
      { label: 'Today', value: runnerTodayLine(runner) },
      ...(runner.slotsLine ? [{ label: 'Slots', value: runner.slotsLine }] : []),
      ...(paused ? [{ label: 'Paused', value: paused }] : []),
      ...(usage ? [{ label: 'Usage', value: usage }] : []),
      ...health
        .filter(item => !USAGE_KEYS.includes(item.key))
        .map(item => ({ label: item.label, value: item.value }))
    ],
    tail: tailOf(runner),
    hint: RUNNER_CARD_MENU_HINT
  }
  const warning = health.find(item => item.warning)
  if (paused && UPDATING.test(paused))
    return withHover({ state: 'running', label: 'Updating', hover, updating: true })
  if (paused) {
    const fromPage = isRunnerPausedFromPage(runner, now)
    return withHover({
      state: 'attention',
      label: fromPage ? 'Paused' : runnerPausedShort(paused),
      hover,
      warning: !fromPage
    })
  }
  if (warning)
    return withHover({
      state: 'attention',
      label: warning.short ?? `${warning.label}: ${warning.value}`,
      hover,
      warning: true
    })
  if (runner.slotsGated)
    return withHover({ state: 'attention', label: 'Taking no work', hover, warning: true })
  const label = [summary, runnerUsageShort(runner.health, now)].filter(Boolean).join(' · ')
  return withHover({ state: 'running', label, hover })
}

/** "12 running: 1 fleet job, 11 your window started", or "Idle". */
function runnerBusyTitle({ total, fleet }: { total: number; fleet: number }): string {
  if (!total) return 'Idle'
  const jobs = `${fleet} fleet ${fleet === 1 ? 'job' : 'jobs'}`
  return total > fleet ? `${total} running: ${jobs}, ${total - fleet} your window started` : `${total} running: ${jobs}`
}

/** From here the card names the 5-hour window, before it is high enough to hold the runner back (that is a warning). */
const RUNNER_CARD_USAGE_PCT = 70

/** "5h 82%" once the current 5-hour window is at RUNNER_CARD_USAGE_PCT or more; null below it, or with a stale reading. */
function runnerUsageShort(health: FleetRunner['health'], now: number): string | null {
  const h5 = health?.usage?.h5
  if (!h5 || h5.pct < RUNNER_CARD_USAGE_PCT || Date.parse(h5.resetsAt) <= now) return null
  return `5h ${Math.round(h5.pct)}%`
}

/**
 * "37 fleet · 81 yours": the fleet's work beside the reviews and fixes of the engineer's own PRs, which the status line
 * leaves out, with the daily cap on the fleet's part when there is one. Skipped runs are not counted.
 */
export function runnerTodayLine(runner: Pick<FleetRunner, 'itemsToday' | 'cap' | 'thanks'>): string {
  return runnerFleetAndOwnLabel(runner) ?? `${runnerItemsTodayLabel(runner)} fleet · 0 yours`
}

/** The pause a /runner window reports while it waits for its running jobs before restarting onto an update. */
const UPDATING = /^updating\b/i

/** The last lines the window or the watch screen shows, under a heading saying which. */
function tailOf(runner: FleetRunner): RunnerCardHover['tail'] {
  const tail = runnerTail(runner)
  if (!tail.length) return undefined
  const title =
    runner.liveView?.tailSource === 'window' ? 'Your /runner window, last lines:' : 'Its watch screen, last lines:'
  return { title, lines: tail }
}

/** The two usage windows, which the hover shows on one line as the status line does. */
const USAGE_KEYS = ['usage-5h', 'usage-7d']

/** "12:00 PM" today, "Sat 1:00 AM" on another day, in this browser's time. */
function resetClock(iso: string, now: number): string {
  const at = new Date(iso)
  const time = at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return at.toDateString() === new Date(now).toDateString()
    ? time
    : `${at.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`
}

/**
 * "5h 37% to 12:00 PM, 7d 31% to Sat 1:00 AM (you@gettattle.com)", as the workspace's status line puts it, the account
 * when the fleet sent it; null with no reading.
 */
export function runnerUsageLine(
  health: FleetRunner['health'],
  now: number,
  account?: string
): string | null {
  const parts = (
    [
      ['5h', health?.usage?.h5],
      ['7d', health?.usage?.d7]
    ] as const
  ).flatMap(([name, window]) =>
    window ? [`${name} ${Math.round(window.pct)}% to ${resetClock(window.resetsAt, now)}`] : []
  )
  if (!parts.length) return null
  return account ? `${parts.join(', ')} (${account})` : parts.join(', ')
}

/** A pause until the 5-hour reset closer than this would barely pause (the fleet refuses it too). */
const USAGE_RESET_MIN_MS = 15 * 60 * 1000
/** Past this the reset is not the current 5-hour window's, so the reading is stale. */
const USAGE_RESET_MAX_MS = 5 * 3600 * 1000 + 10 * 60 * 1000

export type RunnerCardPauseKey = 'pause-1h' | 'pause-4h' | 'pause-usage-reset' | 'pause-tomorrow'

export interface RunnerCardPauseChoice {
  key: RunnerCardPauseKey
  title: string
}

/** What pause offers: 1 hour, 4 hours, until the 5-hour window resets while that is known and not too close, and midnight. */
export function runnerCardPauseChoices(runner: FleetRunner, now: number): RunnerCardPauseChoice[] {
  const resetsAt = runner.health?.usage?.h5?.resetsAt
  const left = resetsAt ? Date.parse(resetsAt) - now : NaN
  const reset: RunnerCardPauseChoice[] =
    resetsAt && left >= USAGE_RESET_MIN_MS && left <= USAGE_RESET_MAX_MS
      ? [
          {
            key: 'pause-usage-reset',
            title: `Until ${resetClock(resetsAt, now)} (your 5-hour window resets)`
          }
        ]
      : []
  return [
    { key: 'pause-1h', title: 'For 1 hour' },
    { key: 'pause-4h', title: 'For 4 hours' },
    ...reset,
    { key: 'pause-tomorrow', title: 'Until midnight (New York time)' }
  ]
}

/**
 * start opens the runner on this computer; cancel stops waiting for it; pause opens its choices; resume goes to the
 * fleet at once.
 */
export type RunnerCardControlKey = 'start' | 'cancel' | 'pause' | 'resume'

export interface RunnerCardControl {
  key: RunnerCardControlKey
  /** The button's name and hover. */
  label: string
  icon: 'power' | 'play' | 'pause' | 'cancel'
  disabled?: boolean
}

/** The hover's last line: what a click on the card does, and what its ⋮ holds. */
export const RUNNER_CARD_MENU_HINT = 'Click to open your runner. ⋮ for slots, stop a run, turn off.'

/**
 * The card's one button, on its left: power while there is no live runner, pause (asking how long) while it takes
 * work, and play to resume once paused from a page. Turning the runner off stays on its menu. While Start is opening the
 * window it offers ✕, since a page cannot tell whether the browser's open-the-app prompt was cancelled. Pause and
 * resume are disabled while an action is still on its way to the laptop.
 */
export function runnerCardControls(
  runner: FleetRunner | null,
  view: Pick<RunnerCardView, 'state'>,
  options: { now: number; sending: boolean }
): RunnerCardControl[] {
  if (!runner?.live) {
    return view.state === 'starting'
      ? [{ key: 'cancel', label: 'Stop waiting: nothing opened', icon: 'cancel' }]
      : [{ key: 'start', label: 'Start your runner on this computer', icon: 'power' }]
  }
  const busy = options.sending || Boolean(runner.pendingAction)
  return isRunnerPausedFromPage(runner, options.now)
    ? [{ key: 'resume', label: 'Resume: take fleet work again', icon: 'play', disabled: busy }]
    : [{ key: 'pause', label: 'Pause…', icon: 'pause', disabled: busy }]
}
